import React, { useEffect, useRef } from "react";
import LabelView from "@components/molecules/LabelView";
import currency from "currency.js";
import { DefaultTheme } from "styled-components/native";
import styled from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { Spacer, VDBText } from "@components/atoms";
import { useTheme } from "styled-components/native";
import { useAppState } from "@redux/slices/appStateSlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { getAvailabilityStatus, hasZeroDecimals } from "./utils";
import { CustomisationKey } from "../types";
import { useGetPdpSectionLayout } from "../../../hooks/useGetSectionLayout";
import { PdpSections } from "@utils/customization";
import { useFilteredSection } from "../../../hooks/useFilteredOptions";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { useState } from "react";
import EmptyImagePreview from "@components/molecules/GalleryView/utils/EmptyImage/EmptyImagePreview";
import { getShapeValue } from "@components/molecules/GalleryView/utils/EmptyImage/utils";
import { Platform, View } from "react-native";
import { Image as ExpoImage } from "expo-image";
import LabelContainer from "./LabelContainer";
import { useTranslation } from "react-i18next";
import { css } from "styled-components/native";
import {
  getBoxShadow,
  isPair,
  isParcel,
  getFormattedStockNumber,
  isMeleeItem,
} from "@utils/product/productValidators";
import {
  SearchFilterTypeEnum,
  SearchFilterTypeEnumLowercase,
} from "@components/atoms/Filters/utils";
import { MediaType, ProductCategory, screenWidth } from "@utils/constants";
import { Diamond } from "@src/types/DiamondDetail";
import Carousel, { ICarouselInstance } from "react-native-reanimated-carousel";
import { configureHorizontalCarouselPanGesture } from "@components/molecules/GalleryView/utils/carouselPanGesture";
import ImagePreview from "@components/molecules/GalleryView/utils/ImagePreview";
import { RenderImageProps } from "@components/molecules/GalleryView/utils/types";
import {
  getImageSource,
  getIOSImageProps,
  getOptimizedImageUrl,
  ImageSizePreset,
} from "@utils/image/imageOptimizer";

const ImageComponent = ExpoImage;

type StickyPriceContainerProps = {
  isGuest: boolean;
  productColor: DefaultTheme;
  itemDetail: Diamond;
  priceMode: PriceModes;
  productType: ProductCategory;
  showAvailabilityDot: boolean;
  productAlphaColor: string;
  productName: string;
  activeIndex: number;
  showProductHeaderStickySummary: boolean;
  setStickyHeaderActiveIndex?: any;
};

const StickyPriceContainer = ({
  isGuest,
  productColor,
  itemDetail,
  priceMode,
  productType,
  showAvailabilityDot,
  productAlphaColor,
  productName,
  activeIndex,
  showProductHeaderStickySummary,
  setStickyHeaderActiveIndex,
}: StickyPriceContainerProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const { isCustomApp } = useAppState();
  const userCurrency = useUserCurrency();
  const userInfo = useUserInfo();
  const hidePriceInPublic =
    userInfo.getFeatureSettings().hide_guest_prices_text;
  const [imageUriError, setImageUriError] = useState(false);
  const topRef = useRef<ICarouselInstance>(null);
  const baseOptions = {
    vertical: false,
    layout: "default",
    width: 250,
    height: 250,
  } as const;

  const stoneType = isPair(itemDetail)
    ? SearchFilterTypeEnum.PAIRS
    : isCustomApp && isParcel(itemDetail)
    ? SearchFilterTypeEnum.PARCEL
    : SearchFilterTypeEnum.SINGLE_STONES;

  const sectionLayout = useGetPdpSectionLayout(
    PdpSections.SUMMARY,
    productType,
    stoneType
  );
  const jewelryConfiguratorLayout = useGetPdpSectionLayout(
    PdpSections.JEWELRY_CONFIGURATOR,
    productType,
    stoneType
  );

  const mediaArray = [
    ...(itemDetail?.three_d_enabled &&
    itemDetail?.three_d_media &&
    itemDetail?.three_d_media.length > 0
      ? [
          {
            type: MediaType.MODEL_3D,
            link: process.env.EXPO_PUBLIC_THREE_D_IFRAME_URL,
            thumb: "",
            order: 1,
          },
          ...(itemDetail?.media ?? []),
        ]
      : itemDetail?.media ?? []),
  ];
  const { color, colorKey } = getAvailabilityStatus(itemDetail?.available);
  const stickyContainerSwitches = jewelryConfiguratorLayout?.sticky_container
    ? Object.values(jewelryConfiguratorLayout?.sticky_container)
        .filter((container) =>
          container.price_modes.includes(userInfo.getPriceMode())
        )
        .map((container) => container.name)
    : [];

  const filteredSections = useFilteredSection(theme, priceMode, sectionLayout);
  const getFilterDetailData = (searchText: string): any => {
    const filteredArray = filteredSections.filter(
      (item) => item.name === searchText
    );

    return filteredArray;
  };
  const objStockNumber = getFilterDetailData(CustomisationKey.stock_no);
  const objStatus = getFilterDetailData(CustomisationKey.status);

  const showStockNumber =
    objStockNumber &&
    objStockNumber?.length !== 0 &&
    stickyContainerSwitches.includes(CustomisationKey.stock_no);

  const showProductBadge = stickyContainerSwitches.includes(
    CustomisationKey.product_badge
  );
  const showShortTitle =
    jewelryConfiguratorLayout?.sticky_container?.short_title?.price_modes?.includes(
      priceMode
    );

  const shouldShowStockContainer = showStockNumber || showProductBadge;

  const isLooseParcel = itemDetail?.diamond_category
    ? itemDetail?.diamond_category ===
      SearchFilterTypeEnumLowercase.LOOSE_PARCEL
    : false;

  const renderIcon = () => {
    if (itemDetail?.image_url && !imageUriError) {
      return (
        <ProductImage
          source={getImageSource(
            getOptimizedImageUrl(itemDetail.image_url, ImageSizePreset.GRID),
            ImageSizePreset.GRID
          )}
          {...getIOSImageProps()}
          onError={() => setImageUriError(true)}
          contentFit={userInfo.disableImageZoom() ? "contain" : "cover"}
        />
      );
    } else {
      return (
        <IconContainer>
          <EmptyImagePreview
            shape={getShapeValue(itemDetail)}
            size={45}
            isPair={isPair(itemDetail) ?? false}
          />
        </IconContainer>
      );
    }
  };

  const getLooseParcelPrice = (item: Diamond | undefined) => {
    const price = item?.hide_price_per_carat
      ? item?.total_sales_price
      : item?.price_per_carat;
    return price
      ? currency(price, {
          symbol: userCurrency,
          precision: userInfo?.roundUpPrices() && !isMeleeItem(item) ? 0 : 2,
        }).format()
      : "-";
  };

  const onSnapToItem = (i: number) => {
    setStickyHeaderActiveIndex?.(i);
  };

  const SummaryDetails = () => {
    return (
      <>
        <StockLabelContainer>
          {showStockNumber && (
            <LabelViewWrapper>
              <VDBText
                type={typography["Body-R"]}
                color={theme["core-06"]}
                selectable={Platform.OS === "web"}
              >
                {t(objStockNumber[0]?.label)}
              </VDBText>
              <Spacer size={SpaceType.Tiny} />
              <View style={{ flex: 1 }}>
                <VDBText
                  type={typography["Body-M"]}
                  color={theme["core-05"]}
                  isBold={true}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  selectable={false}
                >
                  {getFormattedStockNumber(
                    itemDetail?.stock_num,
                    isCustomApp,
                    userInfo.isVdbMarketPlace(),
                    itemDetail?.bmh_request_type
                  ) || "-"}
                </VDBText>
              </View>
            </LabelViewWrapper>
          )}
          {showProductBadge && (
            <LabelContainer
              backgroundColor={productAlphaColor}
              productColor={productColor}
              productName={productName}
              height={20}
            />
          )}
        </StockLabelContainer>
        {showShortTitle && (
          <>
            <Spacer size={SpaceType.Basic} />
            <TextContainer>
              <VDBText
                color={theme["core-05"]}
                type={typography["H2-M"]}
                selectable={Platform.OS === "web"}
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {itemDetail?.short_title}
              </VDBText>
            </TextContainer>
          </>
        )}
        {(isGuest ? !hidePriceInPublic : true) &&
        priceMode !== PriceModes.GREEN ? (
          <>
            <Spacer size={SpaceType.Basic} />
            <LabelView
              itemLeftText={
                productType === ProductCategory.JEWELRY && isCustomApp
                  ? itemDetail?.is_finished_product === false
                    ? t("productDetailPage.forSettingOnly.text")
                    : t("productDetailPage.totalPrice.jewelry.text")
                  : isLooseParcel
                  ? itemDetail.hide_price_per_carat
                    ? t("productDetailPage.pricePerPiece.text")
                    : t("productDetailPage.pricePerCarat.text")
                  : t("productDetailPage.totalPrice.text")
              }
              keyTypographyStyle={typography["Body-R"]}
              valueTypographyStyle={typography["Body-M"]}
              leftIconTheme={
                theme[productColor as keyof DefaultTheme] as string
              }
              itemRightText={
                isLooseParcel
                  ? getLooseParcelPrice(itemDetail)
                  : itemDetail?.total_sales_price
                  ? currency(itemDetail?.total_sales_price, {
                      symbol: userCurrency,
                      precision:
                        (userInfo?.user?.feature_settings?.round_up_prices &&
                          !isMeleeItem(itemDetail)) ||
                        hasZeroDecimals(itemDetail?.total_sales_price)
                          ? 0
                          : 2,
                    }).format()
                  : "-"
              }
              rightPrefixTextTheme={theme["core-05"]}
            />
          </>
        ) : (
          <></>
        )}
      </>
    );
  };

  useEffect(() => {
    if (topRef?.current) {
      topRef.current?.scrollTo({
        index: activeIndex,
        animated: true,
      });
    }
  }, [activeIndex, showProductHeaderStickySummary]);

  const renderItem = ({ item, index }: RenderImageProps) => {
    return (
      <ImagePreview
        index={index}
        isSelected={activeIndex === index}
        item={item}
        activeIndex={activeIndex}
        isSmallDeviceWidth={true}
        deviceWidth={screenWidth}
        resizeImage={true}
        shape={getShapeValue(itemDetail)}
        isMatchModel={false}
        itemID={itemDetail?.id}
        certNum={itemDetail?.cert_num}
        linkedPairChildren={itemDetail?.children}
        threeDMedia={itemDetail?.three_d_media || []}
        GIACertEnable={true}
        fromStickyHeader={true}
        stoneType={stoneType}
        resizeMode={userInfo.disableImageZoom() ? "contain" : "cover"}
      />
    );
  };

  return (
    <>
      {theme.isMobile ? (
        itemDetail?.display_configuration_v2 ? (
          <ConfiguratorParentContainer>
            <ConfiguratorImageContainer>
              {mediaArray && mediaArray.length ? (
                <Carousel
                  {...baseOptions}
                  onConfigurePanGesture={configureHorizontalCarouselPanGesture}
                  data={mediaArray}
                  renderItem={renderItem}
                  enabled={true}
                  snapEnabled={true}
                  pagingEnabled={true}
                  loop={false}
                  ref={topRef}
                  onSnapToItem={onSnapToItem}
                  enableMomentum={true}
                  decelerationRate={0.9}
                  carouselController={true}
                />
              ) : null}
            </ConfiguratorImageContainer>
            <ConfiguratorStockContainer
              showContainer={shouldShowStockContainer}
            >
              {showStockNumber ? (
                <LabelView
                  itemLeftText={t(objStockNumber[0]?.label)}
                  keyTypographyStyle={typography["Body-R"]}
                  valueTypographyStyle={typography["Body-M"]}
                  itemLeftTextTheme={theme["core-06"]}
                  rightPrefixTextTheme={theme["core-05"]}
                  rightIconTheme={color}
                  availabilityIcon={
                    productType !== ProductCategory.JEWELRY &&
                    objStatus &&
                    objStatus?.length !== 0 &&
                    showAvailabilityDot
                      ? colorKey
                      : undefined
                  }
                  ellipsize={false}
                  itemRightText={
                    (!isCustomApp &&
                    itemDetail?.bmh_request_type !== "retail_buy_request"
                      ? " VDB-"
                      : "") + itemDetail?.stock_num
                  }
                  leftPostfixText=""
                />
              ) : (
                <View />
              )}
              {showProductBadge && (
                <LabelContainer
                  backgroundColor={productAlphaColor}
                  productColor={productColor}
                  productName={productName}
                  height={20}
                />
              )}
            </ConfiguratorStockContainer>

            {showShortTitle && (
              <ConfiguratorTitleContainer>
                <VDBText color={theme["core-05"]} type={typography["H2-M"]}>
                  {itemDetail?.short_title}
                </VDBText>
              </ConfiguratorTitleContainer>
            )}
          </ConfiguratorParentContainer>
        ) : (
          <ParentContainer>
            <ImageStockContainer>
              {renderIcon()}
              <StockContainer deviceWidth={screenWidth}>
                <SummaryDetails />
              </StockContainer>
            </ImageStockContainer>
          </ParentContainer>
        )
      ) : (
        <ScrollBehaviourContainer>
          <SummaryDetails />
        </ScrollBehaviourContainer>
      )}
    </>
  );
};

const ParentContainer = styled.View`
  display: flex;
  ${({ theme }) =>
    theme.isMobile &&
    Platform.OS !== "web" &&
    css`
      flex: 1;
    `}
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  background-color: ${({ theme }) => theme["core-01"]};
`;

const ScrollBehaviourContainer = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  border-color: ${({ theme }) => theme["core-03"]};
  border-width: 1px;
  align-content: flex-start;
  border-radius: 8px;
  padding: 16px;
  width: 100%;
`;

const ImageStockContainer = styled.View`
  display: flex;
  flex: 1;
  flex-direction: row;
  gap: 8px;
  width: 100%;
`;

const ProductImage = styled(ImageComponent)`
  width: 88px;
  height: 88px;
`;

const IconContainer = styled.View`
  width: 88px;
  height: 88px;
  background-color: ${({ theme }) => theme["core-02"]};
  justify-content: center;
  align-items: center;
`;

const StockContainer = styled.View<any>`
  display: flex;
  width: 100%;
  justify-content: center;
  padding: 8px 0;
  ${({ theme, deviceWidth }) =>
    theme.isMobile &&
    css`
      width: ${deviceWidth - 104}px;
    `}
`;

const ConfiguratorParentContainer = styled.View`
  width: 100%;
  box-shadow: ${({ theme }) => getBoxShadow(theme.Shadows.Normal)};
  background-color: ${({ theme }) => theme["core-01"]};
`;

const LabelViewWrapper = styled.View`
  flex: 1;
  flex-direction: row;
  min-width: 0;
  margin-right: 8px;
`;

const ConfiguratorImageContainer = styled.View`
  width: 100%;
  height: 250px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
`;

const ConfiguratorStockContainer = styled.View<{
  showContainer: boolean;
}>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${({ showContainer }) =>
    showContainer &&
    css`
      padding: ${SpaceType.Basic}px ${SpaceType.Double}px;
    `}
  border: 1px solid ${({ theme }) => theme["core-03"]};
  width: 100%;
`;

const ConfiguratorTitleContainer = styled.View`
  padding: 8px 16px;
`;

const TextContainer = styled.View`
  width: 100%;
`;

const StockLabelContainer = styled.View`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

export default StickyPriceContainer;
